import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ArrowLeftRight, Send } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function TransferDialog({ open, onOpenChange, card, onTransferred }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => setEmail("");

  const handleTransfer = async () => {
    if (!email.trim()) {
      toast.error("Enter the recipient account email");
      return;
    }
    setLoading(true);
    try {
      await api.post(`/cards/${card.id}/transfer`, { target_email: email.trim() });
      toast.success(`Card wired to ${email.trim()} immediately`);
      reset();
      onOpenChange(false);
      onTransferred();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transfer request failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="bg-[#121216] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display font-black tracking-tight flex items-center gap-2">
            <ArrowLeftRight className="w-5 h-5 text-[#7A5CFF]" /> Transfer Card
          </DialogTitle>
          <DialogDescription className="text-[#A0A0AB]">
            Send this card ({card?.nickname}) to another account. Funds are wired immediately; the
            recipient can confirm afterward to earn reward points.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label className="text-[#A0A0AB] text-xs uppercase tracking-wider">Recipient account email</Label>
            <Input
              data-testid="transfer-email-input"
              placeholder="new.owner@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleTransfer()}
              className="bg-[#1A1A20] border-white/10 text-white h-11"
            />
          </div>
          <div className="rounded-xl border border-[#7A5CFF]/25 bg-[#7A5CFF]/5 p-3">
            <p className="text-xs text-[#A0A0AB] leading-relaxed">
              The card moves to the recipient right away. They'll see it under
              <span className="text-white"> Wired to you</span> and can confirm to collect points.
            </p>
          </div>
          <Button
            data-testid="transfer-submit-button"
            onClick={handleTransfer}
            disabled={loading}
            className="w-full h-12 bg-[#7A5CFF] text-white hover:bg-[#7A5CFF]/90 font-semibold rounded-xl"
          >
            <Send className="w-4 h-4 mr-2" /> {loading ? "Wiring…" : "Wire card now"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
